import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import CatalogHiddenBanner from "../components/CatalogHiddenBanner";
import { Button } from "../components/ui/button";
import { useShop } from "../lib/shop-context";

/**
 * Phase 22.1 — customer AI.
 *
 * The classic search page. When the query is >3 words or ends with "?", we
 * default to AI mode; otherwise we run the deterministic ``/products`` search.
 * The user can toggle either mode explicitly with the "Buscar con IA" chip.
 */
function isNaturalLanguage(q) {
  const s = (q || "").trim();
  if (!s) return false;
  if (s.endsWith("?")) return true;
  return s.split(/\s+/).length > 3;
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const isMaghrebi = params.get("is_maghrebi") === "1";
  const explicit = params.get("mode"); // "ai" | "std" | null
  const aiDefault = isNaturalLanguage(q);
  const useAI = !isMaghrebi && (explicit === "ai" || (explicit !== "std" && aiDefault));

  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const shopCtx = useShop();
  const shop = shopCtx?.shop || "envio";

  useEffect(() => {
    // Phase 24 — is_maghrebi mode: dedicated "Sabores de casa" listing.
    if (isMaghrebi) {
      setLoading(true);
      setSummary("");
      api
        .get("/products", { params: { is_maghrebi: true, limit: 48, shop } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
      return;
    }
    if (!q) {
      setResults([]);
      setSummary("");
      return;
    }
    setLoading(true);
    if (useAI) {
      api
        .post("/ai/search", { query: q, limit: 12, shop })
        .then((r) => {
          setResults(r.data?.products || []);
          setSummary(r.data?.summary || "");
        })
        .catch(() => {
          setResults([]);
          setSummary("");
        })
        .finally(() => setLoading(false));
    } else {
      setSummary("");
      api
        .get("/products", { params: { search: q, limit: 48, shop } })
        .then((r) => setResults(r.data))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }
  }, [q, useAI, isMaghrebi, shop]);

  const setMode = (mode) => {
    const next = new URLSearchParams(params);
    next.set("mode", mode);
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="search-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10">
      {shopCtx?.catalog_hidden ? (
        <CatalogHiddenBanner />
      ) : (
        <>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
        {isMaghrebi ? (
          <>
            <span aria-hidden>🇩🇿</span>{" "}
            <span className="text-[#00953B]">Sabores de casa</span>
          </>
        ) : (
          <>
            Resultados para <span className="text-[#00953B]">“{q}”</span>
          </>
        )}
      </h1>

      {isMaghrebi ? (
        <p className="mt-2 text-neutral-500 text-sm" data-testid="search-maghrebi-subtitle">
          Productos magrebíes seleccionados para tu familia.{" "}
          <span data-testid="search-count">
            {loading
              ? "Cargando…"
              : `${results.length} producto${results.length === 1 ? "" : "s"}`}
          </span>
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            data-testid="search-mode-ai-chip"
            type="button"
            size="sm"
            variant={useAI ? "default" : "outline"}
            onClick={() => setMode("ai")}
            className={`rounded-full gap-1 ${
              useAI ? "bg-[#00953B] hover:bg-[#007a30] text-white" : "border-[#00953B] text-[#00953B]"
            }`}
          >
            <Sparkles className="h-3 w-3" /> Buscar con IA
          </Button>
          <Button
            data-testid="search-mode-std-chip"
            type="button"
            size="sm"
            variant={!useAI ? "default" : "outline"}
            onClick={() => setMode("std")}
            className={`rounded-full ${
              !useAI ? "bg-neutral-900 hover:bg-neutral-800 text-white" : ""
            }`}
          >
            Buscar clásico
          </Button>
          <p className="text-neutral-500 text-sm ml-2" data-testid="search-count">
            {loading
              ? "Buscando…"
              : `${results.length} producto${results.length === 1 ? "" : "s"}`}
          </p>
        </div>
      )}

      {useAI && summary && (
        <p
          data-testid="search-ai-summary"
          className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-neutral-800 max-w-3xl"
        >
          {summary}
        </p>
      )}

      <div className="mt-8">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-xl bg-neutral-100 animate-pulse" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div
            data-testid="search-empty-state"
            className="rounded-2xl border border-dashed border-neutral-300 p-12 text-center"
          >
            <p className="text-lg font-semibold">
              {isMaghrebi
                ? "Aún no hay productos en esta selección"
                : q
                ? "No hemos encontrado productos"
                : "Escribe algo para buscar"}
            </p>
            <p className="text-sm text-neutral-500 mt-1">
              {isMaghrebi
                ? "Vuelve más tarde — estamos ampliando la selección magrebí."
                : q
                ? "Prueba con otras palabras o cambia a otro modo."
                : ""}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {results.map((p) => (
              <div key={p.id} data-testid={`search-result-${p.id}`}>
                <ProductCard product={p} />
                {useAI && p.reason && (
                  <p className="mt-1 text-[11px] text-neutral-500 px-1 line-clamp-2">
                    {p.reason}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
}
